import { TranslationStrings } from 'providers/translations/getTranslations';
import { NavigationConfig } from 'types/navigation';
import { Vertical } from 'utils/vertical';

type VerticalNavigationConfig = {
  [Vertical.PODCASTERS]: NavigationConfig;
  [Vertical.ARTISTS]?: NavigationConfig;
  [Vertical.CONSUMER]?: NavigationConfig;
};

export const verticalMasthead: VerticalNavigationConfig = {
  [Vertical.PODCASTERS]: {
    colorSet: 'encore-inverted-dark-set',
    linksConfig: {
      footer: {
        label: 'Spotify for Podcasters Footer',
        columns: [
          {
            label: 's4p_footer_company',
            entries: [
              {
                nestingLevel: 1,
                title: 'About',
                link: {
                  href: 'https://www.spotify.com/us/about-us/contact',
                  target: '_blank',
                  title: 's4p_footer_about',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Press & Media',
                link: {
                  href: 'https://newsroom.spotify.com',
                  target: '_blank',
                  title: 's4p_footer_press_and_media',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Careers',
                link: {
                  href: 'https://www.lifeatspotify.com/jobs',
                  target: '_blank',
                  title: 's4p_footer_careers',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Contact Us',
                link: {
                  href: 'https://support.spotify.com/podcasters/contact-spotify-anonymous/',
                  target: '_blank',
                  title: 's4p_footer_contact_us',
                  variant: 'text',
                },
              },
            ],
          },
          {
            label: 's4p_footer_creator_tools',
            entries: [
              {
                nestingLevel: 1,
                title: 'Help Center',
                link: {
                  href: 'https://support.spotify.com/podcasters',
                  target: '_blank',
                  title: 's4p_footer_help_center',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Megaphone',
                link: {
                  href: 'https://megaphone.spotify.com',
                  target: '_blank',
                  title: 's4p_footer_megaphone',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Spotify Studios',
                link: {
                  href: 'https://spotify.com/spotifystudios',
                  target: '_blank',
                  title: 's4p_footer_spotify_studios',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Spotify for Artists',
                link: {
                  href: 'https://artists.spotify.com/home',
                  target: '_blank',
                  title: 's4p_footer_spotify_for_artists',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Soundtrap',
                link: {
                  href: 'https://www.soundtrap.com/',
                  target: '_blank',
                  title: 's4p_footer_soundtrap',
                  variant: 'text',
                },
              },
            ],
          },
          {
            label: 's4p_footer_get_the_app',
            entries: [
              {
                nestingLevel: 1,
                title: 'Download on the App Store',
                link: {
                  href: 'https://apps.apple.com/en/app/spotify-for-podcasters/id1056182234',
                  image: {
                    url: 'https://mrkt-web.scdn.co/artists-spotify-com/_next/static/images/footer-ios-a77e2676a3ffba2c521ce743a6d82eea.svg',
                  },
                  target: '_blank',
                  title: 's4p_footer_app_store',
                  variant: 'rectangularImage',
                },
              },
              {
                nestingLevel: 1,
                title: 'Get it on Google Play',
                link: {
                  href: 'https://play.google.com/store/apps/details?id=fm.anchor.android&hl=en_US',
                  image: {
                    url: 'https://mrkt-web.scdn.co/artists-spotify-com/_next/static/images/footer-android-1e8a7815136447656dae5d9e77dfd5ad.svg',
                  },
                  target: '_blank',
                  title: 's4p_footer_google_play',
                  variant: 'rectangularImage',
                },
              },
            ],
          },
        ],
        corporateLinks: [
          {
            label: 'Fine print' as keyof TranslationStrings,
            entries: [
              {
                nestingLevel: 1,
                title: 'Legal',
                link: {
                  href: 'https://www.spotify.com/legal',
                  target: '_blank',
                  title: 's4p_footer_legal',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Privacy Center',
                link: {
                  href: 'https://spotify.com/privacy',
                  target: '_blank',
                  title: 's4p_footer_privacy_center',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Privacy Policy',
                link: {
                  href: 'https://www.spotify.com/legal/privacy-policy',
                  target: '_blank',
                  title: 's4p_footer_privacy_policy',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Cookies',
                link: {
                  href: 'https://www.spotify.com/legal/cookies-policy',
                  target: '_blank',
                  title: 's4p_footer_cookies',
                  variant: 'text',
                },
              },
            ],
          },
        ],
        social: {
          twitter: {
            variant: 'text',
            href: 'https://twitter.com/forpodcasters',
            target: '_blank',
            title: 'Twitter' as keyof TranslationStrings,
          },
          instagram: {
            variant: 'text',
            href: 'https://instagram.com/spotifyforpodcasters',
            target: '_blank',
            title: 'Instagram' as keyof TranslationStrings,
          },
        },
      },
      header: {
        label: 'Spotify for Podcasters Header',
        logo: {
          label: 's4p_logo_label',
          href: 'https://podcasters.spotify.com/',
        },
        centerNav: [
          {
            label: 's4p_navbar_features',
            entries: [
              {
                nestingLevel: 1,
                title: 'Creation tools',
                link: {
                  href: 'https://podcasters.spotify.com/features/create',
                  target: '_self',
                  title: 's4p_navbar_creation_tools',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Video podcasts',
                link: {
                  href: 'https://podcasters.spotify.com/features/video',
                  target: '_self',
                  title: 's4p_navbar_video_podcasts',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Growth tools',
                link: {
                  href: 'https://podcasters.spotify.com/features/grow',
                  target: '_self',
                  title: 's4p_navbar_growth_tools',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Earn',
                link: {
                  href: 'https://podcasters.spotify.com/features/earn',
                  target: '_self',
                  title: 's4p_navbar_earn',
                  variant: 'text',
                },
              },
            ],
          },
          {
            label: 's4p_navbar_resources',
            entries: [
              {
                nestingLevel: 1,
                title: 'News',
                link: {
                  href: 'https://podcasters.spotify.com/resources/news',
                  target: '_self',
                  title: 's4p_navbar_news',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Learn',
                link: {
                  href: 'https://podcasters.spotify.com/resources/learn',
                  target: '_self',
                  title: 's4p_navbar_learn',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Research',
                link: {
                  href: 'https://podcasters.spotify.com/resources/research',
                  target: '_self',
                  title: 's4p_navbar_research',
                  variant: 'text',
                },
              },
              {
                nestingLevel: 1,
                title: 'Community',
                link: {
                  href: 'https://podcasters.spotify.com/resources/community',
                  target: '_self',
                  title: 's4p_navbar_community',
                  variant: 'text',
                },
              },
            ],
          },
          {
            href: 'https://podcasters.spotify.com/switch',
            target: '_self',
            title: 's4p_navbar_switch',
            variant: 'text',
          },
          {
            href: 'https://support.spotify.com/podcasters',
            target: '_self',
            title: 's4p_navbar_help',
            variant: 'text',
          },
        ],
        secondaryNav: [],
        dropdownMenuLabelClose: 'navbar_dropdown_menu_close',
        dropdownMenuLabelOpen: 'navbar_dropdown_menu_open',
        sideMenuPopoverAriaLabel: 'navbar_side_menu_popover_label',
      },
      languageSelection: {
        chooseLanguagePrompt: 'choose_language_prompt',
        globeButtonAriaLabel: 'change_language_label',
      },
    },
  },
};
